import { FC, useRef } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  Stack,
  Textarea,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import isEmpty from 'lodash/fp/isEmpty';
import ReCAPTCHA from 'react-google-recaptcha';
import { Notification } from '../notification';
import { getDataTracking, QUESTIONTYPES } from '../../utils';
import { SelectField } from '../select-field';
import { TrackingLabelProps } from '../../types/global';

export interface QuestionsFormProps extends TrackingLabelProps {
  buttonText?: string;
  hasSubmit?: boolean;
  onSubmit: (data: any) => void;
  questions: any[];
  submitButtonProps?: ButtonProps;
}

export const QuestionsForm: FC<QuestionsFormProps> = ({
  buttonText = 'Submit',
  hasSubmit,
  onSubmit,
  questions = [],
  submitButtonProps,
  trackingLabel,
}) => {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm();

  const captchaRef = useRef<ReCAPTCHA>(null);

  const handleFormSubmit = async (data: any) => {
    const token = await captchaRef?.current?.executeAsync();
    onSubmit({
      ...data,
      [`question_${
        questions.find((q) => q.questionType === QUESTIONTYPES.CAPTCHA)
          ?.questionId
      }`]: token ?? '',
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={4} alignItems="center">
        {questions
          .filter((q) => q.questionText !== 'Add Interests')
          .filter((q) => q.key !== 'QKEY_CPA_AGE')
          .map((q) => {
            const { isReadOnly } = q;

            const uniqueId = q.key ?? q.questionId;
            const questionId = `question_${q.questionId}`;
            const questionAnswers =
              q.questionTypeData?.surveyQuestionData?.availableAnswer ??
              q.questionAnswer;
            const defaultValue =
              !isEmpty(q.responseValue) || typeof q.responseValue === 'number'
                ? q.responseValue
                : null;
            const required = q.questionRequired;
            const isEditable = !isReadOnly;

            if (q.questionType === QUESTIONTYPES.CAPTION) {
              return (
                <Notification
                  key={uniqueId}
                  status="info"
                  message={q.questionText}
                />
              );
            }
            if (q.questionType === QUESTIONTYPES.CAPTCHA) {
              return (
                <Box key={uniqueId} display="none">
                  <ReCAPTCHA
                    ref={captchaRef}
                    size="invisible"
                    sitekey={q.questionTypeData?.recaptchaData?.publicKey}
                  />
                </Box>
              );
            }
            return (
              <FormControl
                key={uniqueId}
                isInvalid={!!errors[questionId]?.message}
                isReadOnly={!isEditable}
              >
                {q.questionText && (
                  <FormLabel htmlFor={uniqueId} fontWeight="semibold">
                    {q.questionText}
                  </FormLabel>
                )}
                {q.questionType === QUESTIONTYPES.TEXT ||
                q.questionType === QUESTIONTYPES.SHORT_TEXT ? (
                  <Input
                    id={uniqueId}
                    defaultValue={defaultValue}
                    disabled={!hasSubmit}
                    readOnly={!isEditable}
                    borderRadius="4px"
                    {...register(questionId, {
                      required: {
                        value: required,
                        message: 'Must enter value',
                      },
                    })}
                  />
                ) : q.questionType === QUESTIONTYPES.LARGE_TEXT ? (
                  // TODO: SJ-theme textarea component
                  <Textarea
                    id={uniqueId}
                    borderColor="brand.monochromatic.200"
                    borderRadius="4px"
                    disabled={!hasSubmit}
                    readOnly={!isEditable}
                    _hover={{ borderColor: 'none' }}
                    _focus={{
                      borderColor: 'brand.monochromatic.800',
                      boxShadow:
                        '0 0 0 4px var(--chakra-colors-brand-additional-focus)',
                      opacity: 0.6,
                    }}
                    defaultValue={defaultValue}
                    {...register(questionId, {
                      required: {
                        value: required,
                        message: 'Must have a value',
                      },
                    })}
                  />
                ) : q.questionType === QUESTIONTYPES.NUMERIC ? (
                  <InputGroup>
                    <Input
                      defaultValue={defaultValue}
                      id={uniqueId}
                      disabled={!hasSubmit}
                      readOnly={!isEditable}
                      type="number"
                      borderRadius="4px"
                      {...register(questionId, {
                        required: {
                          value: required,
                          message: 'Must enter value',
                        },
                      })}
                    />
                  </InputGroup>
                ) : q.questionType === QUESTIONTYPES.MULTI_SINGLE ||
                  q.questionType === QUESTIONTYPES.YES_NO ||
                  q.questionType === QUESTIONTYPES.MULTI_SINGLE_RADIO ? (
                  <>
                    <SelectField
                      id={uniqueId}
                      options={questionAnswers}
                      defaultValue={defaultValue}
                      isDisabled={!hasSubmit || !isEditable}
                      borderRadius="4px"
                      aria-readonly={!isEditable}
                      {...(isEditable ? register(questionId) : {})}
                    />
                    {!isEditable && (
                      <Input
                        type="hidden"
                        defaultValue={defaultValue ?? ''}
                        {...register(questionId)}
                      />
                    )}
                  </>
                ) : null}
                <FormErrorMessage>
                  <>
                    <FormErrorIcon />
                    {errors[questionId]?.message}
                  </>
                </FormErrorMessage>
              </FormControl>
            );
          })}
        {hasSubmit && (
          <Box w={submitButtonProps?.width}>
            <Button
              type="submit"
              colorScheme="blue"
              {...submitButtonProps}
              {...getDataTracking(`${trackingLabel}saveButton`)}
            >
              {buttonText}
            </Button>
          </Box>
        )}
      </Stack>
    </form>
  );
};
